<template>
  <div class="style-selection-step">
    <div class="style-selection-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Style Options -->
      <div class="styles-grid">
        <div
          v-for="style in step.styles"
          :key="style.id"
          class="style-option"
          :class="{ selected: isStyleSelected(style.id) }"
          @click="toggleStyle(style.id)"
        >
          <div class="style-image-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="158" height="223" viewBox="0 0 158 223" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M110 24C110 10.7452 99.255 0 86.0002 0H24C10.7452 0 0 10.7452 0 24V199C0 212.255 10.7452 223 24 223H134C147.255 223 158 212.255 158 199V71.9999C158 58.7451 147.255 48 134 48C120.745 48 110 37.2548 110 24Z" fill="url(#pattern0_2_1501)"/>
              <defs>
                <pattern id="pattern0_2_1501" patternContentUnits="objectBoundingBox" width="1" height="1">
                  <use xlink:href="#image0_2_1501" transform="matrix(0.00158479 0 0 0.00112285 0 -0.0277409)"/>
                </pattern>
                <image id="image0_2_1501" width="631" height="940" preserveAspectRatio="none"
                 :xlink:href="style.image"
                 :alt="style.name" class="style-image" />  
              </defs>
            </svg>
            
            <i :class="`pi ${isStyleSelected(style.id) ? 'pi-heart-fill' : 'pi-heart' } check-icon`"></i>
          </div>
          <span class="style-name">{{ style.name }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'

interface Props {
  step: TasteStep & { type: 'style_selection' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedStyles = computed(() => {
  const data = tasteStore.tasteModal.tasteData[props.step.id] || []
  // Миграция: если в store есть старые данные (string коды), преобразуем их в ID
  if (data.length > 0 && typeof data[0] === 'string') {
    return data.map((codeOrId: string) => {
      const style = props.step.styles.find(
        (s) => s.code === codeOrId || s.id.toString() === codeOrId
      )
      return style?.id
    }).filter((id: any) => id !== undefined)
  }
  return data
})

function isStyleSelected(styleId: number): boolean {
  return selectedStyles.value.includes(styleId)
}

function toggleStyle(styleId: number) {
  const currentStyles = [...selectedStyles.value]
  const index = currentStyles.indexOf(styleId)

  if (index > -1) {
    currentStyles.splice(index, 1)
  } else {
    currentStyles.push(styleId)
  }

  tasteStore.updateTasteData(props.step.id, currentStyles)
}

onMounted(() => {
  // Initialize with existing data if any
  if (!tasteStore.tasteModal.tasteData[props.step.id]) {
    tasteStore.updateTasteData(props.step.id, [])
  }
})
</script>

<style scoped>
.style-selection-step {
  display: flex;
  padding: var(--Padding-XXL, 40px) 120px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  align-self: stretch;
}

.style-selection-content {
  max-width: 800px;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap:8px;
  margin-bottom: 40px;
  text-align: center;
}

.step-title {
  font-family: 'Gilroy';
  font-size: 48px;
  font-weight: 700;
  color: var(--primary-blue-100);
  margin:0;
  line-height: 1.2;
}

.step-subtitle {
  font-family: 'Inter';
  font-size: 20px;
  color: var(--primary-blue-80);
  line-height: 1.3;
  margin: 0;
}

.styles-grid {
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 20px;
  flex: 1 0 0;
  align-self: stretch;
  flex-wrap: wrap;
  height: 400px;
  overflow-y: auto;
}

.style-option {
  display: flex;
  padding: var(--Padding-XS, 4px);
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-XS, 4px);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.20);
  position: relative;

/* Product card */
  box-shadow: 0 0 4px 0px rgba(186, 174, 206, 0.25);

}


.style-image-wrapper {
  display: flex;
  width: 158px;
  height: 223px;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.style-image {
  flex-shrink: 0;
  align-self: stretch;
  aspect-ratio: 158/223;
}


.check-icon {
  display: flex;
  padding: var(--Padding-M, 12px);
  justify-content: center;
  align-items: center;
  gap: 10px;
  position: absolute;
  right: 0;
  border-radius: var(--Corner-radius-infinite, 1000px);
  background: var(--Color-White-80, rgba(255, 255, 255, 0.80));
  backdrop-filter: blur(1.5px);
  font-size:18px;
}

.style-name {
  text-align: left;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.03em;
  padding:8px;
  color: #232F45;
  text-align: center;
}

.style-option.selected .style-name {
  color: var(--active-100);
  font-weight: 600;
}



@media (max-width: 1439px) {
  .styles-grid {
    gap:20px;

  }
}

@media (min-width: 393px) and (max-width: 767px) {
  .style-selection-step {
    padding: 30px 20px 20px 20px;
  }

  
  .styles-grid {
    gap:10px;
    height:439px;
    justify-content: center;
  }

  .styles-grid::-webkit-scrollbar {
    width:0px;
  }

  .title-section {
    margin-bottom: 20px;
    text-align: left;
  }
  .step-title {
    font-size:32px;
  }

  .step-subtitle {
    font-size:15px;
  }

}

@media (min-width: 767px) and (max-width: 1023px) {


  .style-selection-step {
    padding: 30px 20px 20px 20px;
  }

  .styles-grid {
    gap:19px;
    height:504px;
  }

  .styles-grid::-webkit-scrollbar {
    width:0px;
  }

  .title-section {
    margin-bottom: 20px;
    text-align: left;
  }
  .step-title {
    font-size:32px;
  }

  .step-subtitle {
    font-size:15px;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .style-selection-step {
    padding:40px;
  }

  .styles-grid {
    justify-content: center;
    max-height: 343px;
  }

} 

@media (min-width:1440px) and ( max-width:1919px) {
  .style-selection-step {
    padding: 40px;
  }
}

@media (min-width:1919px) {

}
</style>

